import uuidv4 from 'uuid/v4'

/**
 * This class can work together with a react component, what provide react elements and their synthetic events
 * to work together to place signature placeholders on documents.
 *
 * A document means a base DOM element with image elements, what represents the pages of the document.
 * Each page IMG tag must contains the following data attributes:
 * data-pdf-page = <boolean> - must be set to "true", else the page  is not taken in consideration as a document page.
 * data-page-number = <number> - page number, counted from 1 (this value currently out of order)
 *
 * The events of the container elements must be connected with the specific functions of the class:
 * moveStart(), moveStop(), resizeStart(), resizeStop(), containerOnClick(), containerOnMouseMove(), containerOnMouseUp().
 *
 * Most of this event handlers require the event (synthetic event of react) as argument to identify the "currentTarget" elements.
 * Few of the event-handles can work only if the react rendering is done. Current solution is the event handlers are fired using
 * setTimeout() function with 0 millisecond, what cause, the function will arrive in the callback queue of the Javascript Event loop,
 * and will be fired only when the CallStack is empty, what means the render is over. If there is a better solution, like a react hook
 * (componentDidRender - not exists) that is welcome.
 *
 * Can be fetched the created placeholders in an array by ->getPlaceholders().
 * A placeholder have a data-model declared in the class body named: _placeholderTemplate (object).
 * (Take care if you want to extend or modify ot, the database maybe contains already saved data in the original structure.)
 *
 * The functions and class attributes what starts with "_" (underscore) are considered as private, and the rest is public.
 */
export default class SignaturePlaceholderSelector {
    // must populated in constructor
    _stakeholders = []

    // private list of placeholders
    _placeholders = []

    // DOM element id if the container element
    _targetDOMElementId

    _totalPageNumber = 1

    // the visible distance in pixels between he document page IMG tags.
    _pageDistances = 0

    // the array of information about the original PDF page sizes. In order [{pointx, pointy, dpix, dpiy},... ] (this is determined by server API response.)
    _pageSizeList = []

    // contains information about document page IMG tags in form [<DOMRect>, ...]. In order of the pages in DOM.
    _pageImageSizeList = []

    ELEMENT_ID_POSTFIX_FOR_PAGE_NUMBER_VALUE = "_page_number_value"
    MINIMUM_PLACEHOLDER_WIDTH_PTS = 112 // in pixels
    MINIMUM_PLACEHOLDER_HEIGHT_PTS = 70 // in pixels

    // Operation types (actions), what can be made on a placeholder.
    OPERATION_RESIZE = "RESIZE" // in pixels
    OPERATION_MOVE = "MOVE" // in pixels
    OPERATION_NONE = "NONE" // in pixels

    MOUSE_SHIFT_X = 19
    MOUSE_SHIFT_Y = 19

    // Quoted information from other documentations:
    // Page* The page of the document on which the element is placed.
    // The paging works as follows: '1' is the first page of the PDF, '2' the second, and so on. '0' must not be used as page value. To count backwards from the last page, use negative integers: '-1' for the last page, '-2' for the second to last, and so on. Integer
    // Top* How far from the top edge of the document the element is placed.

    // Minimum value is 0. Number (float)
    // Left* How far from the left edge of the document the element is placed.
    // Minimum value is 0. Number (float)

    // Width of the element in pixels.
    // Minimum value is 112.
    // Maximum value is 999999.

    // Height of the element in pixels.
    // Minimum value is 70.
    // Maximum value is 999999

    _placeholderTemplate = {
        id: "",
        x: 0,
        y: 0,
        width: this.MINIMUM_PLACEHOLDER_WIDTH_PTS,
        height: this.MINIMUM_PLACEHOLDER_HEIGHT_PTS,
        signerType: null,
        stakeholderId: null,
        callBackOnDrop: null,
        element: null,
        totalPages: null,
        placedOnPage: null,
        pageSizeOfOriginalPDF: null,
        pageImageInfo: {
            naturalWidth: 0,
            naturalHeight: 0,
            src: '',
            width: 0,
            height: 0,
        },
        // extra
        scrollXToBeVisible: 0,
        scrollYToBeVisible: 0,
    }

    // currently active placeholder, what receive effect of an active operation
    _activePlaceholderId = null

    // currently active operation on the selected/active placeholder
    _activeOperation = this.OPERATION_NONE

    // container DOM element reachable on class instance level
    _containerElement = null

    // a flag to determine fi the react component must notified about the result of an operation to do rerender.
    // This helps to prevent unnecessary rerenders, to keep an inner dropdown opened, or prevent loosing any temporary information,
    // what is not stored in components state
    // what true, the _flushUpdateCallback() function take effect, when it is false, the operation result will not take flush on react virtual DOM.
    _wasPlaceholderModified = false

    // The _flushUpdateCallback is a callback function what must be overwrite by an external callback function.
    // That must contain a render update (setState, dispatch, forceRefresh, ...) to proceed a permanent
    // update on DOM elements. Else the temporary DOM modifications will disappear on the next rendering.
    _flushUpdateCallback = () => { console.warn("No onUpdate function set by external React component") }

    /**
     * This is the constructor of a placeholder selector system.
     *
     * @param {*} stakeholders
     * @param {String} elementId The id of a dome element what parenting the signature placeholder selector
     * @param {*} pageNumber
     * @param {*} pageDistances
     * @param {*} pageSizeList
     * @param {function} flushUpdate
     */
    constructor(stakeholders, elementId, pageNumber, pageDistances, pageSizeList, flushUpdate) {
        this._targetDOMElementId = elementId
        this._totalPageNumber = pageNumber
        this._pageDistances = pageDistances
        this._stakeholders = stakeholders
        this._pageSizeList = pageSizeList
        this._flushUpdateCallback = flushUpdate
    }

    /**
     * Create a new signature placeholder instance.
     * @param {*} placeholderRecord - preset values for placeholder (like the final )
     * @param {Function} callBackOnDrop - optional
     * @returns The new placeholder instance created for a signature.
     */
    createSignaturePlaceholder(placeholderRecord = null, isItActive = true, callBackOnDrop = null, forceAdd = false) {
        if (this._activePlaceholderId && !forceAdd) {
            // console.log("Can not add new element until you don't put on place the active one.")
            return
        }

        // init new placeholder
        let placeholder = this._generatePlaceholder(placeholderRecord)
        // add to the collection list of placeholders
        this._addPlaceholderToTheList(placeholder)

        placeholder.callBackOnDrop = callBackOnDrop

        // set active the new placeholder
        if (isItActive) {
            this._activePlaceholderId = placeholder.id
            this._activeOperation = this.OPERATION_MOVE
        }

        // notify parent component
        this._flushUpdateCallback(this._placeholders)

        return placeholder
    }

    /**
     * This function will update the placeholder in the list of placeholders by iterating the properties.
     * @param {*} placeholderData
     * @returns
     */
    updateSignaturePlaceholder(placeholderData) {
        if (!placeholderData.id) {
            console.warn("Can not update placeholder without id. -> updateSignaturePlaceholder()")
            return
        }

        let placeholder = this.getPlaceholderByIdFromList(placeholderData.id)

        // iterate over the properties of the placeholderData object
        for (let property in placeholderData) {
            if (property === "id") { continue }

            if (placeholderData.hasOwnProperty(property)) {
                // update the property in the target object if it exists
                if (placeholder.hasOwnProperty(property)) {
                    placeholder[property] = placeholderData[property];
                }
            }
        }

        this._setPlaceholderByIdInList(placeholderData.id, placeholder)
        this._flushUpdateCallback(this._placeholders)

        return placeholder
    }

    /**
     * Remove an placeholder element by id.
     * @param {String} id The unique id of a placeholder to remove
     * @returns Bool - true if success, else false (id not found).
     */
    removeSignaturePlaceholder(id) {
        let arrayIndexToRemove = -1

        // find the element to remove
        for (let i = 0; i < this._placeholders.length; i++) {
            if (this._placeholders[i].id === id) {
                arrayIndexToRemove = i;
                break;
            }
        }

        // check if not found the id in the list
        if (arrayIndexToRemove === -1) {
            return false
        }

        // remove the actual element
        this._placeholders.splice(arrayIndexToRemove, 1)

        // if the removed element is the active one, stop to update it
        if (this._activePlaceholderId == id) {
            this._activePlaceholderId = null
        }

        // notify external tool
        this._flushUpdateCallback(this._placeholders)

        return false
    }

    /**
     * This function will clear all the placeholders from class instance and from the DOM as well.
     * Reset all of the operations. Clear flags if there is any.
     */
    deleteAllPlaceholders() {
        this._placeholders = []

        this._activePlaceholderId = null
        this._wasPlaceholderModified = false

        this._stopAnyOperation()

        // notify external tool
        this._flushUpdateCallback(this._placeholders)
    }

    /**
     * Blank click on container, can release an active placeholder
     * @param {*} event
     * @returns
     */
    containerOnClick(event) {
        // let containerElement = event.currentTarget

        if (!this._activePlaceholderId) {
            return
        }

        let placeholder = this.getPlaceholderByIdFromList(this._activePlaceholderId)

        if (!placeholder) {
            return
        }

        // fire callback
        if (typeof placeholder.callBackOnDrop === 'function') {
            placeholder.callBackOnDrop(placeholder)
        }

        // stop updating the placeholder by set it inactive
        this._activePlaceholderId = null
    }

    /**
     * This function is for handling the mouse move, and fetching information fro event.
     * This is a wrapper for the main update function of this class.
     * @param {*} event React synthetic event
     * @returns
     */
    containerOnMouseMove(event) {
        return this._update(event)
    }

    /**
     * This event is good to do an update on react virtual dome.
     * The _flushUpdateCallback is an external callback function, what must
     * contain a render update (setState, dispatch, forceRefresh) to proceed a permanent
     * update on DOM elements
     * @param {*} event Not used yet - react synthetic event
     * @returns
     */
    containerOnMouseUp(event) {
        if (this._wasPlaceholderModified) {
            this._wasPlaceholderModified = false
            return this._flushUpdateCallback(this._placeholders)
        }
    }

    /**
     * This is the start event of the element of the resize button.
     * @param {*} id - placeholder element id (not mongo id, this is the unique id provided by this tool on create)
     */
    resizeStart(id) {
        if (!this._activePlaceholderId) {
            this._activePlaceholderId = id
            this._activeOperation = this.OPERATION_RESIZE
        }
    }

    /**
     * This is the stop event of the element of the resize button.
     */
    resizeStop() {
        this._stopAnyOperation()
    }

    /**
     * This is the start event of the element of the move button.
     * @param {*} id - placeholder element id (not mongo id, this is the unique id provided by this tool on create)
     */
    moveStart(id) {
        if (!this._activePlaceholderId) {
            this._activePlaceholderId = id
            this._activeOperation = this.OPERATION_MOVE
        }
    }

    /**
     * This is the stop event of the element of the move button.
     */
    moveStop() {
        this._stopAnyOperation()
    }

    /**
     * General function what stop any active operation
     */
    _stopAnyOperation() {
        this._activePlaceholderId = null
        this._activeOperation = this.OPERATION_NONE
    }

    /**
     * This is an update method onMouseMove event, this update the placeholder, respecting the
     * operation type
     * @param {*} event
     * @returns
     */
    _update(event) {
        let containerElement = event.currentTarget

        this._pageImageSizeList = this.getAllImageInfo()

        // no active placeholder tha do nothing
        if (!this._activePlaceholderId) {
            return
        }

        let placeholder = this.getPlaceholderByIdFromList(this._activePlaceholderId)

        if (!placeholder) {
            console.error("Placeholder not found in the list by ID: this._activePlaceholderId = ", this._activePlaceholderId)
            console.table(this._placeholders)
            return
        }

        if (!this._pageSizeList || this._pageSizeList.length === 0) {
            console.error("Page size limit is ", this._pageSizeList)
            return
        }

        // This comment is a Quote from StackOverflow what can help when we want to test
        // in different browsers:
        // "Document sizes are a browser compatibility nightmare because,
        // although all browsers expose clientHeight and scrollHeight properties,
        // they don't all agree how the values are calculated.
        // There used to be a complex best-practice formula around for
        // how you tested for correct height/width. This involved using document.documentElement
        // properties if available or falling back on document properties and so on.
        // The simplest way to get correct height is to get all height values
        // found on document, or documentElement, and use the highest one. This is basically what jQuery does:"

        // get scrolling state
        let scrollX = containerElement.scrollLeft // containerElement.scrollY ||
        let scrollY = containerElement.scrollTop //containerElement.scrollX ||

        // prepare debug text for signature
        // let debugCoords = "Signature here! </br>"
        // debugCoords += "Mouse X: " + (x) + ", Y: " + (y) + "</br>"
        // debugCoords += "Iframe H: " + Math.round(height) + ", W: " + Math.round(width) + "</br>"
        // debugCoords += "Iframe scrollX: " + (scrollX) + ", scrollY: " + (scrollY) + "</br>"

        let containerElementViewportOffset = containerElement && containerElement.getBoundingClientRect()
        let containerY = containerElementViewportOffset.top // same as x, but with negative width will be x + width
        let containerX = containerElementViewportOffset.left // same as y, but with negative height will be y + height

        // let containerFullHeight = Math.max(containerElementViewportOffset.height, containerElement.clientHeight, containerElement.scrollHeight, containerElement.offsetHeight);
        // the containerFullHeight does not contain the page margin, so we add one
        // TODO check in all browsers
        // containerFullHeight += this._pageDistances

        let mouseX = (event.clientX*1 || event.pageX*1)
        let mouseY = (event.clientY*1 || event.pageY*1)

        // get mouse coordinates
        let mouseOriginX = mouseX - containerX;
        let mouseOriginY = mouseY - containerY;

        let mouseDocumentPosX = mouseOriginX + scrollX
        let mouseDocumentPosY = mouseOriginY + scrollY

        let mouseIsOnPageNumber = this._getPageNumberByCoordinates(mouseDocumentPosY)

        // placed on page
        placeholder.pageSizeOfOriginalPDF = this._pageSizeList && this._pageSizeList[mouseIsOnPageNumber-1] || {}

        let xMultiplier = this.getXMultiplier(placeholder)
        let yMultiplier = this.getYMultiplier(placeholder)

        // console.log("xMultiplier", xMultiplier)
        // console.log("yMultiplier", yMultiplier)

        // syncing coordinates for active placeholder
        switch (this._activeOperation) {

            // when the placeholder moving
            case this.OPERATION_MOVE: {
                // placed on page
                placeholder.placedOnPage = mouseIsOnPageNumber

                placeholder.x = this.snapXToPageWidth(
                    mouseDocumentPosX - this.MOUSE_SHIFT_X, // displayed scale
                    placeholder.width / xMultiplier, // displayed scale (ptsW to display scale)
                    mouseIsOnPageNumber
                ) * xMultiplier // scale to pts (save the final usable value for PDF, no further calculations for connective)

                let currentPageTop = this.getPageTop(mouseIsOnPageNumber)
                placeholder.y = this.snapYToPageHeight(
                    mouseDocumentPosY - this.MOUSE_SHIFT_Y - currentPageTop,
                    placeholder.height / yMultiplier,
                    mouseIsOnPageNumber
                ) * yMultiplier // scale to pts (save the final usable value for PDF, no further calculations for connective)

                // this checking is permited only in case of move
                placeholder.pageImageInfo = this.getImageInfoUnderCoordinates(mouseX, mouseY)

                // to be able to scroll on the same place in the document to find it.
                placeholder.scrollXToBeVisible = scrollX
                placeholder.scrollYToBeVisible = scrollY

                // set the flag to true to know, must be flushed the modifications
                // this is important, to avoid unnecessary rerenders, what make impossible to use dropdowns inside
                this._wasPlaceholderModified = true

                break
            }

            // when the placeholder is resizing
            case this.OPERATION_RESIZE: {
                let currentPageTop = this.getPageTop(placeholder.placedOnPage)

                let notScaledWidth = mouseDocumentPosX - (placeholder.x / xMultiplier) // displayed pixels scale
                let notScaledHeight = mouseDocumentPosY - (((placeholder.y ) / yMultiplier + currentPageTop)) // displayed pixels scale
                let notScaledX = placeholder.x / xMultiplier // displayed pixels scale
                let notScaledY = ((placeholder.y) / yMultiplier) + currentPageTop // displayed pixels scale

                placeholder.width = this.snapWidthToPageBorder(
                    notScaledX, // displayed pixels scale
                    notScaledWidth, // displayed pixels scale
                    placeholder.placedOnPage,
                    xMultiplier // for scale to pts
                ) * xMultiplier

                placeholder.height = this.snapHeightToPageBorder(
                    notScaledY,  // displayed pixels scale
                    notScaledHeight,  // displayed pixels scale
                    placeholder.placedOnPage,
                    yMultiplier // for scale to pts
                ) * yMultiplier

                // set the flag to true to know, must be flushed the modifications
                // this is important, to avoid unnecessary rerenders, what make impossible to use dropdowns inside
                this._wasPlaceholderModified = true

                break
            }
        }

        placeholder.totalPages = this._totalPageNumber

        // modify directly the DOM elements, to avoid rerender in react, to make persist the changes
        // must call the _flushUpdateCallback.
        this._updateDOMHtml(placeholder, xMultiplier, yMultiplier)
    }

    /**
     * This function put a newly wished X coordinates between the borders of a page.
     * @param {*} x // on displayed scale
     * @param {*} rectangleWidth  // on displayed scale
     * @param {*} pageNumber
     * @returns corrected x in displayed image scale (not natural pixels, not pts)
     */
    snapXToPageWidth(x, rectangleWidth, pageNumber) {
        let pageWidth = this._getPageWidth(pageNumber) // displayed scale
        let result = (x > (pageWidth - rectangleWidth)) ? (pageWidth - rectangleWidth) : x
        return Math.round((result < 0) ? 0 : result)
    }

    /**
     * This function put a newly wished Y coordinates between the borders of a page.
     * @param {*} y
     * @param {*} rectangleHeight
     * @param {*} pageNumber
     * @returns Corrected Y coordinate in pixels on displayed image scale (not natural pixels, not pts)
     * This is relative Y coordinate to the page top.
     */
    snapYToPageHeight(y, rectangleHeight, pageNumber) {
        // get the top of the page
        // let pageBeforeCurrent = (pageNumber-1 > 0) ? pageNumber-1 : 0
        // let upperPageBottom = pageBeforeCurrent !== 0 ? this.getPageBottom(pageBeforeCurrent) : 0 // on displayed scale
        let currentPageTop = this.getPageTop(pageNumber) // on displayed scale
        let currentPageBottom = this.getPageBottom(pageNumber) // on displayed scale

        // calculate absolute height
        y = y + currentPageTop

        // calculate result snapped to the page bottom if that does not fit under the Y coord
        let result = (y > (currentPageBottom - rectangleHeight)) ? (currentPageBottom - rectangleHeight) : y

        // calculate relative Y on current page
        result = result - currentPageTop

        return Math.round((result < 0) ? 0 : result)
    }

    /**
     * This function put a newly wished WIDTH size between the borders of a page.
     * @param {*} x
     * @param {*} rectangleWidth
     * @param {*} pageNumber
     * @param {*} xMultiplier
     * @returns
     */
    snapWidthToPageBorder(x, rectangleWidth, pageNumber, xMultiplier) {
        let pageWidth = this._getPageWidth(pageNumber)  // displayed pixels scale

        let resultRectangleWidth = ((x + rectangleWidth) > pageWidth) ? pageWidth - x : rectangleWidth

        let ptsRectangleWidth = resultRectangleWidth * xMultiplier
        let displaySizeScaledMinWidth = this.MINIMUM_PLACEHOLDER_WIDTH_PTS / xMultiplier
        // put the result in between the minimal limits
        if ((ptsRectangleWidth < (this.MINIMUM_PLACEHOLDER_WIDTH_PTS))) {
            resultRectangleWidth = displaySizeScaledMinWidth
        }

        // rounding result, no half pixels
        return Math.round(resultRectangleWidth)
    }

    /**
     * This function put a newly wished HEIGHT size between the borders of a page.
     * @param {*} y // display scale Y coordinate (absolute) from the (y=)0 coord from the first page
     * @param {*} rectangleHeight // display scale (px)
     * @param {*} currentPageNumber // pae number where is placed the placeholder
     * @param {*} yMultiplier // for scale to PDF pts
     * @returns
     */
    snapHeightToPageBorder(y, rectangleHeight, currentPageNumber, yMultiplier) {
        let currentPageBottom = this.getPageBottom(currentPageNumber)

        let resultRectangleHeight = ((y + rectangleHeight) > currentPageBottom) ? (currentPageBottom - y) : rectangleHeight

        let ptsRectangleHeight = resultRectangleHeight * yMultiplier // convert to PDF pts scale
        let displaySizeScaledMinHeight = this.MINIMUM_PLACEHOLDER_HEIGHT_PTS / yMultiplier // convert to display scale

        // put the result in between minimal limits
        if (ptsRectangleHeight < this.MINIMUM_PLACEHOLDER_HEIGHT_PTS) {
            resultRectangleHeight = displaySizeScaledMinHeight
        }

        // rounding result, no half pixels
        return Math.round(resultRectangleHeight)
    }

    /**
     * This function will calculate the ratio between the original image width and the displayed image width.
     * This is a normalized value, so the 1 means the two images have the same width, 2 means the original image
     * is twice wider than the displayed.
     * @param {*} placeholder
     * @returns
     */
    getXMultiplier(placeholder) {
        let naturalScale = (placeholder.pageImageInfo.naturalWidth / placeholder.pageImageInfo.width)
        let pdfScale = (placeholder.pageSizeOfOriginalPDF.pointx / placeholder.pageImageInfo.naturalWidth) * naturalScale
        return pdfScale
    }

    /**
     * This function will calculate the ratio between the original image height and the displayed image height.
     * This is a normalized value, so the 1 means the two images have the same height, 2 means the original image
     * is twice longer than the displayed.
     */
    getYMultiplier(placeholder) {
        let naturalScale = (placeholder.pageImageInfo.naturalHeight / placeholder.pageImageInfo.height)
        let pdfScale = (placeholder.pageSizeOfOriginalPDF.pointy / placeholder.pageImageInfo.naturalHeight) * naturalScale
        return pdfScale
    }

    /**
     * This function determine the information about an IMG element, what is under the given coordinates.
     * If there are more images under the coordinates, then first match returned.
     * Only marked images detected with specific attributes: "data-pdf-page" = true.
     * No reason yet to check other images that the first matched result.
     * @param {*} x
     * @param {*} y
     * @returns
     */
    getImageInfoUnderCoordinates(x, y) {
        let containerElement = this._getContainer()
        let images = containerElement.getElementsByTagName("img")

        // console.log("getImageInfoUnderCoordinates: containerElement ", containerElement)
        // console.log("getImageInfoUnderCoordinates: images ", images)

        let theCastedImage = {}

        for (let i=0; i < images.length; i++) {
            let oneImage = images[i]

            // filtering out the "none page images", what maybe are used as accessories or tools icons.
            if (!oneImage.getAttribute("data-pdf-page")) {
                continue;
            }

            // check if the image is under the coordinates and break from the loop
            // first match returned
            let rectObject = oneImage.getBoundingClientRect();
            if(y >= rectObject.top && y <= (rectObject.bottom + this._pageDistances) && x >= rectObject.left && x <= rectObject.right) {
                theCastedImage = oneImage
                break
            }
        }

        // console.log("getImageInfoUnderCoordinates: page nr:", theCastedImage.getAttribute("data-page-number"))

        return theCastedImage && {
            naturalWidth: theCastedImage.naturalWidth,
            naturalHeight: theCastedImage.naturalHeight,
            src: theCastedImage.src,
            width: theCastedImage.width,
            height: theCastedImage.height,
        }
    }

    /**
     * Find all marked IMG html tags inside the inner part of the container element.
     * Only marked images detected with specific attributes: "data-pdf-page" = true.
     * All image information about the Bounding Client Rect are pushed in the result array.
     * @returns Array [<DOMRect>, <DOMRect>, ...] in order of elements of DOM.
     */
    getAllImageInfo() {
        let containerElement = this._getContainer()
        let images = containerElement.getElementsByTagName("img")
        let imageInfoList = []

        for (let i=0; i < images.length; i++) {
            let oneImage = images[i]
            if (!oneImage.getAttribute("data-pdf-page")) {
                continue;
            }

            let rectObject = oneImage.getBoundingClientRect();
            imageInfoList.push(rectObject)
        }

        return imageInfoList
    }

    /**
     * Calculate page number using page heights, page distances and Y coordinate of signature.
     * The result never be lower than 1 ( the y = 0 coordinate still the first page).
     * The result never be higher than the total count of pages.
     * @param {*} y pos of the mouse
     * @returns
     */
    _getPageNumberByCoordinates(y) {
        let pageNumber = 1

        // --- original solution ---
        // use this: this._pageSizeList
        // let pagNumber = Math.ceil(y / (onePageHeight + this._pageDistances))

        // --- calculation solution --- not finished
        let accumulativeHeight = 0
        let lastAccumulativeHeight = 0

        for (let i = 0; i < this._pageSizeList.length; i++) {
            let aPageSize = this._pageSizeList[i] // "{\"pointx\":%w,\"pointy\":%h,\"dpix\":%x,\"dpiy\":%y},"
            let anImageSize = this._pageImageSizeList[i] // rectObj

            // save the page bottom on a step before
            lastAccumulativeHeight = accumulativeHeight

            // calculate current page bottom
            accumulativeHeight += anImageSize.height + this._pageDistances

            // check if the Y coord is in between
            if (y > lastAccumulativeHeight && y < accumulativeHeight) {
                pageNumber = i+1
                break;
            }
        }

        // --- img tag attribute value kind solutions --- not working of the mouse is outward the page

        // let containerElement = this._ getContainer()
        // let images = containerElement.getElementsByTagName("img")
        // let theCastedImage = null
        // for (let i=0; i < images.length; i++) {
        //     let oneImage = images[i]
        //     if (!oneImage.getAttribute("data-pdf-page")) {
        //         continue;
        //     }

        //     let rectObject = oneImage.getBoundingClientRect();

        //     if(
        //         y >= rectObject.top && y <= (rectObject.bottom + this._pageDistances) &&
        //         x >= rectObject.left && x <= rectObject.right
        //     ) {
        //         theCastedImage = oneImage
        //         break
        //     }
        // }

        // pageNumber = theCastedImage && theCastedImage.getAttribute("data-page-number") || 0

        pageNumber = pageNumber < 1 ? 1 : pageNumber
        pageNumber = pageNumber > this._totalPageNumber ? this._totalPageNumber : pageNumber

        return pageNumber
    }

    // can help when cross browser testing got fails
    // getScrollXY(baseElement) {
    //     let scrOfX = 0, scrOfY = 0;
    //     if( typeof( baseElement.pageYOffset ) == 'number' ) {
    //       //Netscape compliant
    //       scrOfY = baseElement.pageYOffset;
    //       scrOfX = baseElement.pageXOffset;
    //     } else if( baseElement && ( baseElement.scrollLeft || baseElement.scrollTop ) ) {
    //       //DOM compliant
    //       scrOfY = baseElement.scrollTop;
    //       scrOfX = baseElement.scrollLeft;
    //     } else if( baseElement && ( baseElement.scrollLeft || baseElement.scrollTop ) ) {
    //       //IE6 standards compliant mode
    //       scrOfY = baseElement.scrollTop;
    //       scrOfX = baseElement.scrollLeft;
    //     }
    //     return [ scrOfX, scrOfY ];
    //   }

    /**
     * Get the relative Y coordinate (measured from the container) of the current page bottom.
     * Page distances included.
     * @param {*} pageNumber
     * @returns
     */
    getPageBottom(pageNumber) {
        // --- calculate by img tag marker ---
        // let bottom = null
        // let containerElement = this._ getContainer()
        // let images = containerElement.getElementsByTagName("img")

        // for (let i=0; i < images.length; i++) {
        //     let oneImage = images[i]
        //     if (!oneImage.getAttribute("data-pdf-page")) {
        //         continue;
        //     }

        //     if( pageNumber === oneImage.getAttribute("data-page-number") ) {
        //         let rectObject = oneImage.getBoundingClientRect();
        //         bottom = rectObject.bottom
        //         break
        //     }
        // }

        // --- calculation solution --- not finished
        let accumulativeHeight = 0
        for (let i = 0; i < this._pageSizeList.length; i++) {
            let aPageSize = this._pageSizeList[i] // "{\"pointx\":%w,\"pointy\":%h,\"dpix\":%x,\"dpiy\":%y},"
            let anImageSize = this._pageImageSizeList[i] // rectObj
            //accumulativeHeight += aPageSize.pointy
            accumulativeHeight += anImageSize.height + this._pageDistances

            if (pageNumber === i+1) {
                break
            }
        }

        // last _pageDistances removed for the correct bottom coordinate.
        return accumulativeHeight - this._pageDistances
    }

    /**
     * Get the relative Y coordinate (measured from the container) of the current page top.
     * Page distances included.
     * @param {*} pageNumber
     * @returns
     */
    getPageTop(pageNumber) {
        let accumulativeHeight = 0
        let lastPageHeight = 0
        for (let i = 0; i < this._pageSizeList.length; i++) {
            let aPageSize = this._pageSizeList[i] // "{\"pointx\":%w,\"pointy\":%h,\"dpix\":%x,\"dpiy\":%y},"
            let anImageSize = this._pageImageSizeList[i] // rectObj
            //accumulativeHeight += aPageSize.pointy
            accumulativeHeight += anImageSize.height + this._pageDistances
            lastPageHeight = anImageSize.height
            if (pageNumber === i+1) {
                break
            }
        }

        // last _pageDistances removed for the correct bottom coordinate.
        return accumulativeHeight - this._pageDistances - lastPageHeight
    }

    /**
     * This function will give the specified page width in pixels.
     * @param {*} pageNumber
     * @returns width in pixels on display scale
     */
    _getPageWidth(pageNumber) {
        let width = 0

        for (let i = 0; i < this._pageSizeList.length; i++) {
            let aPageSize = this._pageSizeList[i] // "{\"pointx\":%w,\"pointy\":%h,\"dpix\":%x,\"dpiy\":%y},"
            let anImageSize = this._pageImageSizeList[i] // rectObj

            if (pageNumber === i+1) {
                width = anImageSize.width
                break
            }
        }

        return width
    }

    /**
     * Return the instance of placeholder by id.     *
     * @param {String} id An unique id generated on placeholder creation of this tool. Not a mongo _id.
     * @returns
     */
    getPlaceholderByIdFromList(id) {
        let index = -1

        // find the element to remove
        for (let i = 0; i < this._placeholders.length; i++) {
            if (this._placeholders[i].id === id) {
                index = i;
                break;
            }
        }

        // check if not found the id in the list
        if (index === -1) {
            return null
        }

        return this._placeholders[index]
    }

    /**
     * This function will replace an instance of placeholder in the list of placeholder by ID.
     * No safety checks, just replace if found.
     * @param {String} id An unique id generated on placeholder creation of this tool. Not a mongo _id.
     * @param {object} newPlaceholder the new instance of the placeholder object
     * @returns Return the instance of placeholder updated
     */
    _setPlaceholderByIdInList(id, newPlaceholder) {
        let index = -1

        // find the element to remove
        for (let i = 0; i < this._placeholders.length; i++) {
            if (this._placeholders[i].id === id) {
                index = i;
                break;
            }
        }

        // check if not found the id in the list
        if (index === -1) {
            return null
        }

        return this._placeholders[index] = newPlaceholder
    }

    /**
     * Return all placeholders.
     * @returns Array contains instances of placeholders.
     */
    getPlaceholders() {
        return this._placeholders
    }

    /**
     * Add new placeholder to th
     * @returns
     */
    _generatePlaceholder(placeholderRecord = null) {
        // update actual IMG data
        this._pageImageSizeList = this.getAllImageInfo()

        // clone the template
        let placeholder = JSON.parse(JSON.stringify(this._placeholderTemplate))

        // setup the new instance
        // this will be overwrite when the placeholderRecord is an already existing reloaded record
        placeholder.id = this._getNewPlaceholderIndex()

        // must set if exists else will added an unused
        if (placeholderRecord) {
            placeholder.stakeholderId = placeholderRecord.stakeholderId
            placeholder.signerType = placeholderRecord.signerType
        }

        placeholder.placedOnPage = 1
        placeholder.pageSizeOfOriginalPDF = this._pageSizeList && this._pageSizeList[placeholder.placedOnPage-1] || {}
        // current page info
        placeholder.pageImageInfo = this._pageImageSizeList[placeholder.placedOnPage-1]

        let xMultiplier = this.getXMultiplier(placeholder)
        let yMultiplier = this.getYMultiplier(placeholder)

        placeholder.width = this.MINIMUM_PLACEHOLDER_WIDTH_PTS
        placeholder.height = this.MINIMUM_PLACEHOLDER_HEIGHT_PTS

        if (!placeholder.stakeholderId && this._stakeholders.length > 0) {
            // set a new id
            let unusedSHolder = this._getUnusedStakeholder()
            placeholder.stakeholderId = unusedSHolder && unusedSHolder._id || ""
            placeholder.signerType = unusedSHolder && unusedSHolder.signerType || ""
        }

        // update with initial values or exiting data
        if (placeholderRecord) {
            placeholder = {
                ...placeholder,
                ...placeholderRecord
            }
        }

        return placeholder
    }

    /**
     * this function returns with a stakeholder what is not used yet by any of the placeholders.
     * @returns StakeholderRecord or null
     */
    _getUnusedStakeholder() {
        let freeStakeholder = null

        for (let i=0; i < this._stakeholders.length; i++) {
            let oneStake = null

            let isItAlreadyInUse = false
            for (let j=0; j < this._placeholders; j++) {
                let onePlace = this._placeholders[j]
                if (oneStake._id === onePlace.stakeholderId) {
                    isItAlreadyInUse = true
                }
            }

            if (!isItAlreadyInUse) {
                freeStakeholder = oneStake
                break
            }
        }

        // set a new id
        return freeStakeholder
    }

    /**
     * Modify coordinates and texts in the signature placeholder rectangle what are changing.
     * @param {*} placeholder
     */
    _updateDOMHtml(placeholder, xMultiplier, yMultiplier) {
        let labelElementForPageNumberValue = window.document.getElementById(placeholder.id + this.ELEMENT_ID_POSTFIX_FOR_PAGE_NUMBER_VALUE)
        labelElementForPageNumberValue.innerHTML = placeholder.placedOnPage + "/" + this._totalPageNumber

        let currentPageTop = this.getPageTop(placeholder.placedOnPage)

        let signPlaceholderElement = null
        if (this._activePlaceholderId) {
            signPlaceholderElement = window.document.getElementById(this._activePlaceholderId)
            signPlaceholderElement.style.left = placeholder.x / xMultiplier + "px"
            signPlaceholderElement.style.top = ((placeholder.y) / yMultiplier + currentPageTop) + "px"
            signPlaceholderElement.style.width = (placeholder.width / xMultiplier) + "px"
            signPlaceholderElement.style.height = (placeholder.height / yMultiplier) + "px"
        }

        // can be extended with more values to show in the rectangle
    }

    /**
     * This function provide a new unique placeholder id.
     * @returns
     */
    _getNewPlaceholderIndex() {
        return "signaturePlaceholder_" + uuidv4()
    }

    /**
     * Add placeholder to the list of the class instance
     * @param {*} placeholder
     */
    _addPlaceholderToTheList(placeholder) {
        this._placeholders.push(placeholder)
    }

    /**
     *
     * @returns
     */
    _getContainer() {
        let result = window.document.getElementById(this._targetDOMElementId)
        //console.log("_ getContainer window.document.getElementById(this._targetDOMElementId) = ", result)
        return result
    }
}



// WEBPACK FOOTER //
// ./components/DSDocuments/handleSignaturePlaceholders/signaturePlaceholderSelector.jsx